import { createAsyncThunk } from '@reduxjs/toolkit';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import {
  CreateDepartmentParams,
  CheckDepartmentWasAssignParams,
  UpdateOnboardingUserByAdminParams,
  FileUploadParams,
  OAuthAuthorizeParams,
  ByIdParams,
  LoginAsParams,
  ApiResponse,
  LoginParams,
  LoginResponse,
  FileUploadResponse,
} from '../types';

// Login Action
const login = createAsyncThunk<LoginResponse, LoginParams>(
  `/login`,
  async (params, { rejectWithValue }) => {
    try {
      const response = (await jwtAxios.post(
        `${endPoints.login}`,
        params,
      )) as ApiResponse<LoginResponse>;
      return response.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  },
);

const loginAsAccount = createAsyncThunk<any, LoginAsParams>(
  `/loginAsAccount`,
  async (params, { rejectWithValue }) => {
    try {
      const response =
        params?.roles === 'admin'
          ? await jwtAxios.post(`${endPoints.loginAsAdmin}`, params)
          : params?.roles === 'agency'
            ? await jwtAxios.post(`${endPoints.loginAsAgency}`, params)
            : params?.roles === 'subcontractor'
              ? await jwtAxios.post(`${endPoints.loginAsEmployee}`, params)
              : null;
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const loginAsByParentCompany = createAsyncThunk(
  `/loginAsByParentCompany`,
  async (
    params: { companyParentId: string; roles: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.loginAsByParentCompany}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const forgotPassword = createAsyncThunk<any, { email: string }>(
  `/forgotPassword`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.forgotPassword}`,
        params,
      );

      // Explicitly type the response data
      const data: ApiResponse<any> = response.data;

      return data.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  },
);

const resetPassword = createAsyncThunk(
  `/resetPassword`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.patch(
        `${endPoints.resetPassword}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const oAuthAuthorize = createAsyncThunk<any, OAuthAuthorizeParams>(
  `/oAuthAuthorize`,
  async (payload, { rejectWithValue }) => {
    try {
      const { cb, params } = payload;
      const response = await jwtAxios.get(`${endPoints.oauth}`, { params });
      if (cb) cb(response?.data?.code);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getMe = createAsyncThunk(`/getMe`, async (_, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(`${endPoints.getMe}`);
    return response;
  } catch (e) {
    return rejectWithValue(e);
  }
});

const getAppConfigs = createAsyncThunk(
  `/getAppConfigs`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.appConfigs}`);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getOnboarding = createAsyncThunk(
  `/getOnboarding`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.prospects.prospectOnboarding.onboarding}`,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getPermissions = createAsyncThunk(
  `/getPermissions`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.getPermissions}`);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const logout = createAsyncThunk<null, void>(
  `/logout`,
  async (): Promise<null> => {
    return null;
  },
);

const uploadFile = createAsyncThunk<FileUploadResponse, FileUploadParams>(
  `/uploadFile`,
  async ({ file }, { rejectWithValue }) => {
    try {
      let fileType = file.type;
      if (file.type === '' && file.name.endsWith('.msg')) {
        fileType = 'application/vnd.ms-outlook';
      } else fileType = fileType || 'application/pdf';

      const fileExt = file?.name?.split('.')?.length
        ? file?.name?.split('.')?.pop()
        : null;
      const params = {
        fileType,
        fileExt,
      };
      const signS3Response = await jwtAxios.get(`${endPoints.uploadFile}`, {
        params,
      });
      const { url } = signS3Response?.data as FileUploadResponse;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const uploadFileSignal = createAsyncThunk<FileUploadResponse, FileUploadParams>(
  `/uploadFileSignal`,
  async ({ file, signal }, { rejectWithValue }) => {
    try {
      const fileType = file?.type || null;
      const fileExt = file?.name?.split('.')?.length
        ? file?.name?.split('.')?.pop()
        : null;
      const params = {
        fileType,
        fileExt,
      };
      const signS3Response = await jwtAxios.get(`${endPoints.uploadFile}`, {
        params,
      });
      const { url } = signS3Response?.data as FileUploadResponse;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
        signal,
      });
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const signup = createAsyncThunk(
  `/signup`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(`${endPoints.signup}`, params);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/**
 * Body sent when the signed-in employee saves an onboarding step: the step
 * bookkeeping plus whatever fields that step collects.
 */
export type OnboardingStepPayload = {
  currentStep: string;
  progressStep: string;
} & Record<string, any>;

const createOnboarding = createAsyncThunk(
  `/createOnboarding`,
  async (params: OnboardingStepPayload, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.prospects.prospectOnboarding.onboardingV2}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const updateOnboarding = createAsyncThunk(
  `/updateOnboarding`,
  async (params: OnboardingStepPayload, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.prospects.prospectOnboarding.onboardingV2}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const updateOnboardingNationality = createAsyncThunk(
  `/updateOnboardingNationality`,
  async (params: OnboardingStepPayload, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.prospects.prospectOnboarding.onboardingNationality}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const updateOnboardingVerifyId = createAsyncThunk(
  `/updateOnboardingVerifyId`,
  async (params: OnboardingStepPayload, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.prospects.prospectOnboarding.onboardingVerifyId}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const updateOnboardingSuccess = createAsyncThunk(
  `/updateOnboardingSuccess`,
  async (params: OnboardingStepPayload, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.prospects.prospectOnboarding.onboardingSuccess}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getSignatureYoti = createAsyncThunk(
  `/getSignatureYoti`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.prospects.prospectOnboarding.yoti}`,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const checkStateVerifiedYoti = createAsyncThunk(
  `/checkStateVerifiedYoti`,
  async (params: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.prospects.prospectOnboarding.yoti}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const onboardingSendMailZoho = createAsyncThunk(
  `/onboardingSendMailZoho`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.prospects.prospectOnboarding.onboarding}/zoho/send-email/v2`,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getStatusZoho = createAsyncThunk(
  `/getStatusZoho`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.prospects.prospectOnboarding.onboarding}/zoho/status/v2`,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const verifyZoho = createAsyncThunk(
  `/verifyZoho`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.patch(
        `${endPoints.prospects.prospectOnboarding.onboarding}/zoho/verify`,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getAccountList = createAsyncThunk(
  `/getAccountList`,
  async (
    params: {
      page: number;
      size: number;
      search: string;
      role: string;
      // NX-5713 filters — an omitted key means "do not filter on this".
      isPartner?: boolean;
      // Several tags narrow the list; axios repeats the key per value.
      tag?: string[];
      accountType?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.get(`${endPoints.user.listUser}`, {
        params,
      });
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/** NX-5713: every tag in use, so the filter is not limited to the loaded page. */
const getCompanyParentTags = createAsyncThunk(
  `/getCompanyParentTags`,
  async (_, { rejectWithValue }) => {
    try {
      return await jwtAxios.get(`${endPoints.companyParent.tags}`);
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

/** NX-5713: super-admin edits to an instance from Manage Account. */
const updateCompanyParent = createAsyncThunk<
  any,
  {
    companyParentId: string;
    isPartner?: boolean;
    tags?: string[];
    companyName?: string;
    enabledPayeFTH?: boolean;
  }
>(
  `/updateCompanyParent`,
  async ({ companyParentId, ...body }, { rejectWithValue }) => {
    try {
      return await jwtAxios.patch(
        `${endPoints.companyParent.base}/${companyParentId}`,
        body,
      );
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const acceptRejectAdmin = createAsyncThunk<any, ByIdParams>(
  `/acceptRejectAdmin`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.user.acceptReject}/${params?.id}`,
        params?.params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const bockAdmin = createAsyncThunk<any, ByIdParams>(
  `/bockAdmin`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.user.blockUnblock}/${params?.id}`,
        params?.params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const createAdmin = createAsyncThunk(
  `/createAdmin`,
  async (
    params: {
      company: string;
      email: string;
      forename: string;
      surname: string;
      phone?: string;
      userType?: string;
      primaryCountry?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      if (params.userType === 'employer') {
        const response = await jwtAxios.post(
          `${endPoints.user.superAdminCreateEmployer}`,
          {
            company: params.company,
            forename: params.forename,
            surname: params.surname,
            email: params.email,
            phone: params.phone,
            payrollCountry: params.primaryCountry,
          },
        );
        return response;
      }
      const response = await jwtAxios.post(
        `${endPoints.user.superAdminCreate}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getBureauSettings = createAsyncThunk<any, ByIdParams>(
  `/getBureauSettings`,
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.bureauSettings}/${id}/company-parent`,
        {
          params,
        },
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const updateBureauSettings = createAsyncThunk<any, ByIdParams>(
  `/updateBureauSettings`,
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.bureauSettings}/${id}/company-parent`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const logoutSupperAdmin = createAsyncThunk(
  `/logoutSupperAdmin`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.logoutSupperAdmin}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const switchCountry = createAsyncThunk<
  any,
  {
    username: string;
    payrollCountry: string;
    userType: string;
    isSuperAdmin: boolean;
  }
>(`/switchCountry`, async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.post(`${endPoints.switchCountry}`, params);
    return response;
  } catch (e) {
    return rejectWithValue(e);
  }
});

const getListDepartment = createAsyncThunk(
  `/getListDepartment`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.department.main, {
        params,
      });
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const createDepartment = createAsyncThunk<
  ApiResponse<any>,
  CreateDepartmentParams
>('/createDepartment', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.post(endPoints.department.main, params);
    return response.data;
  } catch (e: any) {
    return rejectWithValue(e.response?.data?.message || e.message);
  }
});

const checkDepartmentWasAssign = createAsyncThunk<
  ApiResponse<any>,
  CheckDepartmentWasAssignParams
>('/checkDepartmentWasAssign', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.department.checkAssign, {
      params,
    });
    return response.data;
  } catch (e: any) {
    return rejectWithValue(e.response?.data?.message || e.message);
  }
});

const updateOnboardingUserByAdmin = createAsyncThunk<
  ApiResponse<any>,
  UpdateOnboardingUserByAdminParams
>('/updateOnboardingUserByAdmin', async (params, { rejectWithValue }) => {
  try {
    const { id, body } = params;
    const response = await jwtAxios.put(
      `${endPoints.prospects.prospectOnboarding.updateOnboardingAdmin}/${id}`,
      body,
    );
    return response.data;
  } catch (e: any) {
    return rejectWithValue(e.response?.data?.message || e.message);
  }
});

const createOnboardingUserByAdmin = createAsyncThunk(
  '/createOnboardingUserByAdmin',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.prospects.prospectOnboarding.createOnboardingAdmin}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const updateOnboardingSuccessByAdmin = createAsyncThunk(
  `/updateOnboardingSuccessByAdmin`,
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.prospects.prospectOnboarding.onboardingAdminSuccess}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const processYearEnd = createAsyncThunk(
  `/processYearEnd`,
  async (params: { p60Date?: string | null }, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.companyParent.processYearEnd}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getCurrentYearEndProcess = createAsyncThunk(
  `/getCurrentYearEndProcess`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.companyParent.currentYearEndProcess}`,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const appAct = {
  login,
  logout,
  forgotPassword,
  resetPassword,
  getMe,
  getAppConfigs,
  uploadFile,
  uploadFileSignal,
  getPermissions,
  signup,
  loginAsAccount,
  loginAsByParentCompany,
  getOnboarding,
  createOnboarding,
  updateOnboarding,
  updateOnboardingNationality,
  updateOnboardingVerifyId,
  updateOnboardingSuccess,
  getSignatureYoti,
  checkStateVerifiedYoti,
  onboardingSendMailZoho,
  getStatusZoho,
  verifyZoho,
  getAccountList,
  updateCompanyParent,
  getCompanyParentTags,
  acceptRejectAdmin,
  bockAdmin,
  createAdmin,
  getBureauSettings,
  updateBureauSettings,
  logoutSupperAdmin,
  switchCountry,
  getListDepartment,
  createDepartment,
  checkDepartmentWasAssign,
  updateOnboardingUserByAdmin,
  createOnboardingUserByAdmin,
  updateOnboardingSuccessByAdmin,
  processYearEnd,
  getCurrentYearEndProcess,
  oAuthAuthorize,
};
